/**
 * uni-push 通用插件 —— 核心实现
 * 职责：初始化推送、获取 clientId、注册消息监听、解析 payload、按配置分发处理
 * 业务逻辑（路由映射、消息处理、cid 上报等）全部通过 UniPushOptions 注入，本文件不含任何项目代码
 */
import type {
  PushAlertOptions,
  PushLocalNotification,
  PushMessage,
  PushPayloadData,
  PushState,
  UniPushOptions,
} from './types';
import { playPushAlert } from './alert';
import { PushEventType } from './types';

/** clientId 默认本地缓存 key（与 UniPushOptions.clientIdCacheKey 默认值一致） */
export const DEFAULT_CLIENT_ID_CACHE_KEY = 'push_client_id';

// 模块级记录最新 cid（init 成功后更新），供 getCachedPushCid 优先返回
let lastClientId = '';

/**
 * 获取当前推送 clientId（内存优先，冷启动未就绪时回退本地缓存）
 * 典型用途：登录/注册/退出等接口上报 cid，供后端绑定/解绑别名标签
 * H5、小程序或 cid 未就绪时返回空字符串，后端按空值忽略即可
 */
export function getCachedPushCid(cacheKey: string = DEFAULT_CLIENT_ID_CACHE_KEY): string {
  if (lastClientId) return lastClientId;
  try {
    return uni.getStorageSync(cacheKey) || '';
  }
  catch {
    return '';
  }
}

export class UniPushService {
  private options: UniPushOptions;
  private state: PushState = {
    clientId: '',
    initStatus: 'idle',
    initError: '',
    isListening: false,
  };

  constructor(options: UniPushOptions) {
    this.options = options;
  }

  /**
   * 初始化推送服务（获取 clientId + 注册消息监听）
   * H5 端 uni.getPushClientId 可能不触发回调，调用方不要 await 阻塞启动流程
   */
  async init() {
    if (this.state.isListening) return;

    this.setState({ initStatus: 'pending' });

    try {
      // 获取 clientId（失败说明 uni-push 未开通/未集成，manifest 的 unipush 配置
      // 必须位于 app-plus.distribute.sdkConfigs.push.unipush，否则此处会静默失败）
      await this.initClientId();
    }
    catch (err: any) {
      this.setState({
        initStatus: 'failed',
        initError: err?.errMsg || err?.message || String(err ?? ''),
      });
      throw err;
    }

    // uni-push 2.0 中 uni.onPushMessage 已覆盖 App 前台透传(receive)与点击(click)两类事件，
    // 不要再叠加 plus.push.addEventListener，否则同一条消息会被重复处理
    uni.onPushMessage((res) => {
      // 原始事件日志：能看到这条说明监听链路通畅（type=receive 即透传送达）
      this.log(`onPushMessage 原始事件 type=${res.type}`, res);
      this.handleMessage({
        title: res.data.title,
        content: res.data.content,
        payload: res.data?.payload as string || '',
      }, res.type as PushEventType);
    });

    this.setState({ isListening: true, initStatus: 'success', initError: '' });
    this.log('初始化完成, clientId:', this.state.clientId);

    // 检查系统通知权限（Android 13+ 默认拒绝，未开启时所有通知都无法展示）
    this.checkNotificationPermission();
  }

  /**
   * 检查系统通知权限（仅 App 端）
   *
   * 应用商店合规要求（华为等）：用户拒绝通知权限后 App 不得再主动弹框，
   * 只能在 App 设置中提供入口由用户自行开启（本项目入口：设置 -> 消息通知 / 系统权限管理）。
   *
   * 因此不做任何本地缓存标记，直接判断权限状态：
   * - 已授权：无需处理；
   * - 用户已拒绝：永不再主动弹框；
   * - 从未请求过：触发一次系统权限弹框（Android 13+）。
   * 任何情况下都不弹 App 级对话框。
   */
  checkNotificationPermission() {
    if (this.options.permissionPrompt === false) return;
    // #ifdef APP-PLUS
    try {
      const setting = uni.getAppAuthorizeSetting();
      this.log('通知权限状态:', setting.notificationAuthorized);
      if (setting.notificationAuthorized === 'authorized') return;
      if (plus.os.name !== 'Android') {
        // iOS：注册推送时系统首次自动弹授权框；拒绝后不再主动弹框（合规要求）
        return;
      }
      // Android 的 getAppAuthorizeSetting 只返回 authorized/denied，
      // 无法区分「从未请求」与「用户拒绝」，故用系统原生 API 精确识别：
      const main = plus.android.runtimeMainActivity() as any;
      const perm = 'android.permission.POST_NOTIFICATIONS';
      // 已授权（含 Android 13 以下安装时默认授予）
      if (main.checkSelfPermission(perm) === 0) return;
      // 用户拒绝过：合规要求不得再主动弹框，由用户在「设置 - 消息通知」自行开启
      if (main.shouldShowRequestPermissionRationale(perm)) return;
      // 走到这里有两种情况：从未请求（会弹系统框）、勾选了「不再询问」的永久拒绝（请求也不会弹框）
      plus.android.requestPermissions([perm], () => {}, () => {});
    }
    catch (e) {
      this.log('通知权限检查异常:', e);
    }
    // #endif
  }

  /**
   * 统一处理推送消息（点击事件 或 前台接收事件），可供测试页手动构造消息调用
   */
  handleMessage(msg: PushMessage, eventType: PushEventType = PushEventType.CLICK) {
    const isClick = eventType === PushEventType.CLICK;
    this.log(`收到${isClick ? '点击(click)' : '透传(receive)'}消息:`, msg);
    if (!msg) return;

    const data = this.parsePayload(msg);

    // 原始钩子（宿主项目记录 lastMessage、埋点等）
    this.options.onMessage?.(msg, eventType, data);

    if (isClick) {
      this.handleUserClick(data, msg);
    }
    else {
      // 透传消息完整链路日志：payload 解析结果 -> 补偿通知 -> 业务分发
      this.log('[receive] payload 解析结果:', data, '| type =', data?.type ?? '(无)');
      // App 在线时推送不会自动弹系统通知，需手动创建通知栏消息；
      // 用户点击该通知会再次触发 onPushMessage（type=click），复用点击跳转逻辑
      this.createLocalNotification(msg, data);
      this.handleReceive(data, msg);
    }
  }

  /** 设置 App 角标（仅 App 端；部分 Android ROM 不支持，tabbar 角标请用 uni.setTabBarBadge） */
  setBadge(count: number) {
    // #ifdef APP-PLUS
    plus.runtime.setBadgeNumber(count);
    // #endif
  }

  /** 清除 App 角标 */
  clearBadge() {
    this.setBadge(0);
  }

  /**
   * 获取推送运行时状态（用于调试/页面展示，判断 uni-push 是否启用并完成初始化）
   */
  getStatus() {
    return {
      ready: this.state.initStatus === 'success' && !!this.state.clientId && this.state.isListening,
      ...this.state,
    };
  }

  /**
   * 获取 clientId
   * 缓存仅作为兜底展示值，每次启动仍调用 API 刷新（卸载重装/清缓存后 cid 会变化）
   */
  private initClientId() {
    const cacheKey = this.options.clientIdCacheKey || DEFAULT_CLIENT_ID_CACHE_KEY;

    // 先用缓存兜底，避免等待回调期间 clientId 为空
    try {
      const cachedCid = uni.getStorageSync(cacheKey);
      if (cachedCid) this.setState({ clientId: cachedCid });
    }
    catch { /* 存储不可用时静默 */ }

    return new Promise<string>((resolve, reject) => {
      uni.getPushClientId({
        success: (res) => {
          this.setState({ clientId: res.cid });
          lastClientId = res.cid;
          try {
            uni.setStorageSync(cacheKey, res.cid);
          }
          catch { /* 存储不可用时静默 */ }
          // 通知宿主项目（如上报 cid 绑定用户）
          this.options.onClientId?.(res.cid);
          resolve(res.cid);
        },
        fail: (err) => {
          // 失败说明 uni-push 未开通/未集成，缓存的旧 cid 不可信
          console.error('[UniPush] 获取ClientID失败', err);
          reject(err);
        },
      });
    });
  }

  /** 解析 payload（增强容错：兼容字符串/对象，解析失败不阻断流程） */
  private parsePayload(msg: PushMessage): PushPayloadData | null {
    if (!msg.payload) {
      this.log('payload 为空，按无业务数据处理');
      return null;
    }
    try {
      return typeof msg.payload === 'string' ? JSON.parse(msg.payload) : { ...msg.payload };
    }
    catch (e) {
      console.error('[UniPush] Payload 解析失败，原始值:', msg.payload, e);
      return null;
    }
  }

  /** 处理用户点击通知栏的行为 */
  private handleUserClick(data: PushPayloadData | null, msg: PushMessage) {
    // 项目自定义点击处理，返回 true 表示已接管，跳过默认跳转
    if (this.options.onClick?.(data, msg) === true) return;

    // 无业务数据 / 无路由解析器，默认跳首页
    const path = data ? (this.options.resolveRoute?.(data) || '') : '';
    if (path) {
      this.navigate(path);
    }
    else {
      this.goHome();
    }
  }

  /** 处理 App 在前台时收到的透传消息：按 payload.type 分发到项目处理器 */
  private handleReceive(data: PushPayloadData | null, msg: PushMessage) {
    // 通知提醒（声音/震动）：由 payload 提醒字段 + 宿主项目设置开关共同决定
    this.triggerAlert(data);

    const handler = data?.type ? this.options.receiveHandlers?.[data.type] : undefined;
    if (handler) {
      this.log(`[receive] 命中 receiveHandlers['${data!.type}']，执行业务处理`);
      handler(data, msg);
      this.log(`[receive] receiveHandlers['${data!.type}'] 处理完成`);
    }
    else if (this.options.onReceive) {
      this.log(`[receive] 未命中 receiveHandlers（type=${data?.type ?? '(无)'}），走 onReceive 兜底`);
      this.options.onReceive(data, msg);
    }
    else {
      console.warn(`[UniPush][receive] 未配置处理器，消息被丢弃: type=${data?.type ?? '(无)'}`);
    }
  }

  /**
   * 按 payload 提醒字段播放声音/震动（需配置 options.alert 才启用；
   * 字段全部缺省 = 静默，保证后端未下发提醒字段时向后兼容）
   */
  private triggerAlert(data: PushPayloadData | null) {
    if (!this.options.alert) return;
    // payload 的提醒字段（后端约定，见推送开发文档）；缺省均为 false/空 = 静默
    const alertFields = (data ?? {}) as PushAlertOptions;
    const { sound = false, customSound = '', forceSound = false, vibrate = false } = alertFields;
    if (!sound && !customSound && !vibrate) return;
    this.log('[alert] 触发提醒:', { sound, customSound, forceSound, vibrate });
    playPushAlert({ sound, customSound, forceSound, vibrate }, this.options.alert.getSettings?.());
  }

  /** App 在线时创建本地补偿通知（仅 App 端） */
  private createLocalNotification(msg: PushMessage, data: PushPayloadData | null) {
    const { createNotification = true } = this.options;
    if (createNotification === false) return;

    let notification: PushLocalNotification | false;
    if (typeof createNotification === 'function') {
      notification = createNotification(msg, data);
      if (notification === false) return;
    }
    else {
      // 纯透传消息可能没有 title/content，从 payload 取或用默认值兜底
      notification = {
        title: msg.title || (data as any)?.title || '新消息',
        content: msg.content || (data as any)?.content || '您有一条新消息，请查看',
        payload: msg.payload,
      };
    }

    // #ifdef APP-PLUS
    uni.createPushMessage({
      ...notification,
      success: () => this.log('本地通知创建成功'),
      fail: err => console.error('[UniPush] 本地通知创建失败', err),
    });
    // #endif
  }

  /** 执行跳转：默认 navigateTo，失败依次降级 switchTab / reLaunch */
  private navigate(path: string) {
    if (this.options.navigate) {
      this.options.navigate(path);
      return;
    }
    uni.navigateTo({
      url: path,
      fail: () => {
        uni.switchTab({
          url: path,
          fail: () => uni.reLaunch({ url: path }),
        });
      },
    });
  }

  private goHome() {
    uni.switchTab({
      url: this.options.homePage,
      fail: () => uni.reLaunch({ url: this.options.homePage }),
    });
  }

  /** 更新内部状态并通知宿主项目 */
  private setState(partial: Partial<PushState>) {
    Object.assign(this.state, partial);
    this.options.onStateChange?.({ ...this.state });
  }

  private log(...args: any[]) {
    if (this.options.debug === false) return;
    console.log('[UniPush]', ...args);
  }
}

/** 创建推送服务实例（各项目在接入层调用，传入自己的业务配置） */
export function createUniPush(options: UniPushOptions) {
  return new UniPushService(options);
}
